import { useCallback, useEffect, useMemo, useState } from "react";
import { FiEdit2, FiPlus, FiSearch, FiTrash2 } from "react-icons/fi";
import UnitModal from "../../components/masters/UnitModal";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import ClientPagination, { useClientPagination } from "../../components/common/ClientPagination";

export default function UnitsMasterPage() {
  const { accessToken } = useAuth();

  const [units, setUnits] = useState([]);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);

  const loadUnits = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const response = await apiRequest("/units", { accessToken });

      setUnits(Array.isArray(response) ? response : response?.data || []);
    } catch (requestError) {
      setError(requestError.message || "Unable to load units.");
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadUnits();
  }, [loadUnits]);

  const filteredUnits = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) return units;

    return units.filter((unit) =>
      [unit.name, unit.code, unit.symbol].some((value) =>
        value?.toLowerCase().includes(query),
      ),
    );
  }, [search, units]);

  const stats = useMemo(() => {
    const active = units.filter((unit) => unit.isActive).length;

    return {
      total: units.length,
      active,
      inactive: units.length - active,
    };
  }, [units]);
  const unitsPagination = useClientPagination(filteredUnits);

  const handleSave = async (form) => {
    setError("");

    try {
      if (editingUnit) {
        await apiRequest(`/units/${editingUnit.id}`, {
          method: "PATCH",
          accessToken,
          body: form,
        });
      } else {
        await apiRequest("/units", {
          method: "POST",
          accessToken,
          body: {
            code: form.code,
            name: form.name,
            symbol: form.symbol,
            decimalPlaces: form.decimalPlaces,
          },
        });
      }

      setShowModal(false);
      setEditingUnit(null);
      await loadUnits();
    } catch (requestError) {
      setError(requestError.message || "Unable to save unit.");
    }
  };

  const handleDelete = async (unit) => {
    if (
      !window.confirm(
        `Delete "${unit.name}"? This will fail if products still use this unit.`,
      )
    ) {
      return;
    }

    setError("");

    try {
      await apiRequest(`/units/${unit.id}`, {
        method: "DELETE",
        accessToken,
      });

      await loadUnits();
    } catch (requestError) {
      setError(requestError.message || "Unable to delete unit.");
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUnit(null);
  };

  return (
    <div>
      <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm text-gray-500">Masters / Units</p>
          <h1 className="mt-1 text-3xl font-bold text-gray-900">
            Units Management
          </h1>
          <p className="mt-2 text-gray-500">
            Manage quantity units used by products and inventory.
          </p>
        </div>

        <button
          type="button"
          onClick={() => {
            setEditingUnit(null);
            setShowModal(true);
          }}
          className="flex h-11 items-center justify-center gap-2 rounded-xl bg-primary-800 px-5 text-white hover:bg-primary-900"
        >
          <FiPlus />
          Add Unit
        </button>
      </div>

      {error && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="mb-8 grid gap-5 md:grid-cols-3">
        <div className="rounded-3xl border bg-white p-6">
          <p className="text-sm text-gray-500">Total Units</p>
          <h2 className="mt-2 text-3xl font-bold">{stats.total}</h2>
        </div>

        <div className="rounded-3xl border bg-white p-6">
          <p className="text-sm text-gray-500">Active</p>
          <h2 className="mt-2 text-3xl font-bold text-green-600">
            {stats.active}
          </h2>
        </div>

        <div className="rounded-3xl border bg-white p-6">
          <p className="text-sm text-gray-500">Inactive</p>
          <h2 className="mt-2 text-3xl font-bold text-red-500">
            {stats.inactive}
          </h2>
        </div>
      </div>

      <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <div className="border-b border-gray-100 p-5">
          <div className="relative max-w-md">
            <FiSearch
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search units..."
              className="h-12 w-full rounded-xl border border-gray-200 pl-12 pr-4 outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Unit
                </th>
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Code
                </th>
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Symbol
                </th>
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Decimal Places
                </th>
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">
                  Status
                </th>
                <th className="px-6 py-4 text-right text-xs uppercase text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-12 text-center text-sm text-gray-500"
                  >
                    Loading units...
                  </td>
                </tr>
              ) : filteredUnits.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-12 text-center text-sm text-gray-500"
                  >
                    No units found.
                  </td>
                </tr>
              ) : (
                unitsPagination.pageItems.map((unit) => (
                  <tr
                    key={unit.id}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 font-semibold text-gray-900">
                      {unit.name}
                    </td>
                    <td className="px-6 py-4">
                      <span className="rounded-lg bg-gray-100 px-2.5 py-1 font-mono text-sm text-gray-700">
                        {unit.code}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-700">{unit.symbol}</td>
                    <td className="px-6 py-4 text-gray-700">
                      {unit.decimalPlaces}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${
                          unit.isActive
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-600"
                        }`}
                      >
                        {unit.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingUnit(unit);
                            setShowModal(true);
                          }}
                          className="flex h-9 w-9 items-center justify-center rounded-lg border text-gray-600 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-800"
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(unit)}
                          className="flex h-9 w-9 items-center justify-center rounded-lg border text-red-500 hover:border-red-200 hover:bg-red-50"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <ClientPagination pagination={unitsPagination} label="units" />
      </div>

      <UnitModal
        open={showModal}
        onClose={closeModal}
        onSave={handleSave}
        initialData={editingUnit}
      />
    </div>
  );
}
